import { isGradient } from "@components/color-picker/lib/detect";

import { gradientBorderCss, safeGradient } from "@/shared/utils/gradient-css";
import { COMBO_ANIM_BASE_OPACITY } from "@library/constants";
import { hasBorder } from "@library/shared";
import styled, { css } from "styled-components";

type ComboDesignedProps = {
  $verticalAlignment: string;
  $horizontalAlignment: string;
  $backgroundColor: string;
  $borderColor: string;
  $round: string;
  $borderWidth: number;
  $minHeight: string;
  $maxWidth: string;
  $comboName: string;
  $overlayOpacity?: string;

  $animation?: {
    type: "block" | "elements";
    speed: "slow" | "medium" | "fast";
    baseOpacity: string;
    direction: "forwards" | "backwards";
  };
  $isSorting?: boolean;
  $insertPosition?: "before" | "after";
};

type AnimationType = {
  $animation?: {
    type: "block" | "elements";
    speed: "slow" | "medium" | "fast";
    baseOpacity: string;
    direction: "forwards" | "backwards";
  };
};

const textAlignment: Record<string, string> = {
  start: "left",
  center: "center",
  end: "right",
};

export const ComboStyled = styled.div<ComboDesignedProps>`
  &.comboWrapper {
    ${({ $verticalAlignment, $horizontalAlignment }) => css`
      justify-content: ${$horizontalAlignment || "inherit"};
      align-items: ${$verticalAlignment || "inherit"};
      ${textAlignment[$horizontalAlignment] &&
      css`
        text-align: ${textAlignment[$horizontalAlignment]};
      `};
    `}
    .inner_wrapper {
      --combo-max-width: 1440px;
      max-width: var(--combo-max-width);
      background: ${({ $comboName, $backgroundColor }) => {
        if ($comboName === "FullScreen") return "none";
        if (!$backgroundColor) return "transparent";
        if (isGradient($backgroundColor)) {
          return safeGradient($backgroundColor) ?? "transparent";
        }
        return $backgroundColor;
      }};
      justify-content: ${({ $horizontalAlignment }) => $horizontalAlignment};
      align-items: ${({ $verticalAlignment }) => $verticalAlignment};
      ${({ $borderColor, $borderWidth }) => {
        if (
          !hasBorder({ borderColor: $borderColor, borderWidth: $borderWidth })
        )
          return;
        return gradientBorderCss($borderColor, $borderWidth);
      }};
      border-radius: ${({ $round }) => ($round ? $round : "0")};
      ${({ $minHeight }) => {
        switch ($minHeight) {
          case "narrow":
            return css`
              min-height: 0;
              padding: var(--combo-padding-narrow-y)
                var(--combo-padding-narrow-x);
              @media screen and (max-width: 768px) {
                padding: var(--mobile-combo-padding-narrow-y)
                  var(--mobile-combo-padding-narrow-x);
              }
            `;
          case "auto":
            return css`
              min-height: 0;
            `;
          case "screenFit":
            return css`
              min-height: var(--sd-max-vh, 100vh);
            `;
        }
      }};

      ${({ $maxWidth, $comboName }) => {
        if ($comboName === "FullScreen")
          return css`
            max-width: 100%;
          `;
        switch ($maxWidth) {
          case "narrow":
            return css`
              --combo-max-width: 720px;
            `;
          case "medium":
            return css`
              --combo-max-width: 1140px;
            `;
          case "wide":
            return css`
              --combo-max-width: 1440px;
            `;
          case "full":
            return css`
              --combo-max-width: 100%;
            `;
          default:
            return css`
              --combo-max-width: 1440px;
            `;
        }
      }};

      ${({ $comboName }) => {
        if ($comboName === "Narrator") {
          return css`
            padding: 0;
          `;
        }
      }};

      > div {
        ${({ $minHeight }) => {
          if (["auto", "narrow"].includes($minHeight)) {
            return css`
              min-height: 0;
            `;
          }
        }};
      }
    }
    ${({ $isSorting, $insertPosition }) =>
      $isSorting &&
      css`
        &::after {
          content: "";
          position: absolute;
          left: 0;
          width: 100%;
          height: 2px;
          background-color: #610cff;
          opacity: 0;
          visibility: hidden;
          transition-property: opacity, visibility;
          transition-duration: 0.2s;
          transition-timing-function: ease-in-out;
          ${$insertPosition === "before" &&
          css`
            top: -4px;
            opacity: 1;
            visibility: visible;
          `}
          ${$insertPosition === "after" &&
          css`
            bottom: -4px;
            opacity: 1;
            visibility: visible;
          `}
        }
      `}
  }

  .comboBackground {
    display: ${({ $comboName }) =>
      $comboName === "FullScreen" ? "block" : "none"} !important;
    &:before {
      background: ${({ $backgroundColor }) => $backgroundColor || "#ffffff"};
      opacity: ${({ $overlayOpacity }) => Number($overlayOpacity) / 100};
    }
  }
`;

export const Container = styled.div<AnimationType>`
  ${({ $animation }) => {
    if (!$animation) return;
    let duration = 1000;
    if ($animation.speed === "slow") duration = 2000;
    if ($animation.speed === "fast") duration = 700;

    const opacity =
      Number($animation.baseOpacity ?? COMBO_ANIM_BASE_OPACITY) / 100 || 0;

    return css`
      --combo-anim-dur: ${duration}ms;
      --anim-opacity: ${opacity};
      .setInnerWrapper {
        opacity: var(--anim-opacity);
        &[data-animated="true"] {
          transition: opacity var(--combo-anim-dur) 0s ease;
          opacity: 1;
        }
      }

      ${$animation.type === "elements" &&
      css`
        &:not([data-combo-name="Array"]) {
          .setInnerWrapper {
            opacity: 1;
          }
          .element-wrapper-XNCgh1eTFF {
            opacity: var(--anim-opacity);
            &[data-animated="true"] {
              opacity: 1;
              transition: opacity var(--combo-anim-dur) 0s ease;
            }
          }
        }
        &[data-animated="true"] {
          .setInnerWrapper,
          .element-wrapper-XNCgh1eTFF {
            opacity: 1;
          }
        }
      `}
    `;
  }};
`;
